import { Injectable, inject } from '@angular/core';
import { AuthService } from '../auth/services/auth.service';
import { AnalyticsService } from './analytics.service';
import { StorageService } from './storage.service';
import { APP_CONFIG } from '../config/app-config.token';
import { AppConfig } from '../config/app-config.model';

/**
 * UserContextService
 *
 * Single entry point for user identity, backed by OAuth2/OIDC (angular-oauth2-oidc).
 *
 * It previously abstracted a dual auth system so callers did not have to branch on the
 * useModernAuth flag. The legacy keycloak-angular lane was removed on 2026-08-26 once
 * every environment had run on modern auth since 2026-04-15; this service kept its role
 * as the single place the UI asks "who is the user".
 *
 * ```typescript
 * const isInternalUser = this.userContext.isInternalUser();
 * ```
 *
 * Note: API call branching (/{inss} vs /me) is handled by using dual service methods (get*() vs getMy*()).
 * This service is primarily for UI conditional rendering and user information access.
 */
@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  private authService = inject(AuthService);
  private analytics = inject(AnalyticsService);
  private storage = inject(StorageService);
  private config = inject<AppConfig>(APP_CONFIG);


  /**
   * Check if the current user is an internal user (ABVV staff).
   *
   * Internal users:
   * - Authenticate via Keycloak internal client
   * - Can search and view any member's data
   * - Use /{inss} API endpoints
   * - Default route: /search
   *
   * External users:
   * - Authenticate via Belgian FAS
   * - Can only access their own data
   * - Use /me API endpoints (via getMy*() service methods)
   * - Default route: /membership
   */
  isInternalUser(): boolean {
    return this.authService.isInternalUser();
  }

  /**
   * Get the current user's INSS (Belgian social security number).
   * Returns null if not available.
   *
   * Format: 11 digits (YYMMDDXXXCC)
   * Example: "85041822361"
   */
  getInss(): string | null {
    return this.authService.getInss();
  }

  /**
   * Get the current user's display name.
   * Returns null if not available.
   */
  getDisplayName(): string | null {
    return this.authService.getDisplayName();
  }

  getGivenName(): string | null {
    return this.authService.getGivenName();
  }

  /**
   * Get the current user's identifier (user_id for internal, inss for external).
   * This is used for display purposes in the UI.
   * Returns null if not available.
   */
  getUserIdentifier(): string | null {
    if (this.isInternalUser()) {
      return this.getUserId();
    }
    return this.getInss();
  }

  /**
   * Get the current user's internal user ID.
   * This is typically the employee ID for internal users.
   * Returns null if not available.
   */
  getUserId(): string | null {
    const profile = this.authService.getUser();
    return profile?.belgianClaims?.userId || null;
  }

  /**
   * Get the current user's locale.
   * Returns 'nl' or 'fr', defaults to 'nl' if not available.
   */
  getLocale(): string | null {
    const profile = this.authService.getUser();
    return profile?.['locale'] || null;
  }

  /**
   * Observable of user profile changes (modern auth only).
   * Useful for subscribing to locale changes after authentication.
   */
  get userProfile$() {
    return this.authService.userProfile$;
  }

  /**
   * Logout the current user (single entry point for the whole UI).
   * Tracks the event, clears per-member UI state, then redirects to the
   * appropriate logout page based on the auth system.
   */
  logout(): void {
    this.analytics.trackEvent('logout');
    const inss = this.getInss();
    if (inss) {
      this.storage.removeUnreadMessagesPopUp(inss);
    }
    this.authService.logout();
  }
}
